import React from "react";
import { ScheduleConflictItem } from "@myapp/contract";
import Modal from "../../overlay/Modal/Modal";
import { Button } from "../../controls/Button/Button";
import { ConflictItemList } from "../ConflictItemList/ConflictItemList";

/**
 * "This overlaps — save anyway?" Every caller reaches it through a 409 `SCHEDULE_CONFLICT`, whose
 * payload is the `conflicts` list, and confirms by resending the same request with `force: true`.
 *
 * The copy is overridable because the sentence differs per owner: an absence displaces work, an
 * appointment is displaced by it. Defaults keep the absence wording the Zeitmanagement pages
 * already ship.
 */

const DEFAULT_TITLE = "Terminüberschneidung erkannt";
const DEFAULT_DESCRIPTION =
  "Diese Abwesenheit überschneidet sich mit folgenden Einträgen. Möchten Sie trotzdem " +
  "fortfahren? Die betroffenen Termine werden zur Neuplanung markiert.";

/**
 * An absence always wins the overlap — it is never the one flagged. Every default AND
 * caller-supplied `description` above was written for the opposite direction ("the absence
 * displaces this, and THAT gets marked"), so when one of the conflicts IS an absence this has to
 * override both: the absence stays exactly where it is, and it is the block being saved that
 * gets marked for Umdisponierung instead.
 */
const ABSENCE_BLOCKS_DESCRIPTION =
  "Diese Zeit überschneidet sich mit einer Abwesenheit (Urlaub/Krankheit/Schulung/Privatzeit). " +
  "Abwesenheiten haben Vorrang und werden nicht verschoben. Möchten Sie trotzdem fortfahren? " +
  "Dieser Eintrag wird stattdessen zur Neuplanung markiert.";

type Props = {
  open: boolean;
  conflicts: ScheduleConflictItem[];
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
};

export const ConflictDialog: React.FC<Props> = ({
  open,
  conflicts,
  onConfirm,
  onCancel,
  isLoading = false,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  confirmLabel = "Trotzdem speichern",
}) => {
  const effectiveDescription = conflicts.some((c) => c.type === "ABSENCE")
    ? ABSENCE_BLOCKS_DESCRIPTION
    : description;

  return (
    <Modal
      empty
      show={open}
      setShow={(val) => { if (!val && !isLoading) onCancel(); }}
      // `empty` skips the header, so the dialog has no title to be labelled by.
      ariaLabel={title}
      className="flex max-h-[90vh] w-[520px] flex-col gap-4 overflow-hidden p-6"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              stroke="#D97706"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p className="text-base font-semibold text-gray-900">{title}</p>
      </div>

      <p className="text-sm text-gray-600">{effectiveDescription}</p>

      <ConflictItemList items={conflicts} />

      <div className="flex gap-3">
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          className="flex-1 rounded-[30px] bg-amber-600 font-bold text-white h-[46px]"
          type="button"
        >
          {isLoading ? "Speichern..." : confirmLabel}
        </Button>
        <Button
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 rounded-[30px] bg-[#716D6B] font-bold text-white h-[46px]"
          type="button"
        >
          Abbrechen
        </Button>
      </div>
    </Modal>
  );
};

export default ConflictDialog;
